import { useState } from 'react'
import { toast } from 'sonner'
import { captureException } from '@sentry/react'
import { Plus, Trash2 } from 'lucide-react'
import { useNavigate } from 'react-router'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'tailwind-v4/components/dialog'
import { Button } from 'tailwind-v4/components/button'
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldSeparator,
} from 'tailwind-v4/components/field'
import { Input } from 'tailwind-v4/components/input'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from 'tailwind-v4/components/input-group'
import { NativeSelect } from 'tailwind-v4/components/native-select'
import { Spinner } from 'tailwind-v4/components/spinner'
import { UserAvatar } from 'tailwind-v4/components/user-avatar'
import morpheusApi from '@/state/morpheus/morpheus.slice'
import billingApi from '@/state/billing/billing.slice'
import { type OrganizationType } from '@/state/morpheus/types/organisation'
import useUser from '@/hooks/useUser'
import { getReferralOrgId } from '@/utils/referral'
import {
  ATTRIBUTION_STORAGE_KEY,
  clearAttributionCookie,
  readAttribution,
  readAttributionCookie,
  type SignupAttribution,
} from '@/utils/attribution'
import { extractErrorMessage } from '@/utils/extract-error-message'

const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-500', '500+'] as const
const MAX_VENUES = 15

// The trial subscription is created directly with Stripe, but the local
// `subscriptions` row is only populated later via the
// `customer.subscription.created` webhook — so `GET .../subscription` can
// 404 for a few seconds after `createTrialSubscription` resolves. Poll for
// it rather than dropping the customer into an org that looks unsubscribed.
const SUBSCRIPTION_POLL_INTERVAL_MS = 2000
const SUBSCRIPTION_POLL_TIMEOUT_MS = 60_000

function normaliseWebsite(value: string) {
  const trimmed = value.trim()
  if (!trimmed) return trimmed
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
}

function wait(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

interface VenueField {
  id: string
  name: string
  url: string
}

interface FormState {
  name: string
  website: string
  venues: VenueField[]
  companySize: string
  jobTitle: string
  referralCode: string
}

function newVenueField(): VenueField {
  return { id: crypto.randomUUID(), name: '', url: '' }
}

const initialState: FormState = {
  name: '',
  website: '',
  venues: [newVenueField()],
  companySize: '',
  jobTitle: '',
  referralCode: '',
}

function VenueFieldsList({
  venues,
  invalid,
  onChangeName,
  onChangeUrl,
  onRemove,
}: {
  venues: VenueField[]
  invalid: boolean
  onChangeName: (id: string, name: string) => void
  onChangeUrl: (id: string, url: string) => void
  onRemove: (id: string) => void
}) {
  const rows = venues.map((venue, index) => (
    // Tight gap within a venue (name + its optional website) vs. the
    // looser gap between venues below — spacing is what tells the two
    // fields apart from the next venue's, not a border.
    <div key={venue.id} className="flex flex-col gap-2">
      <InputGroup className="h-10">
        <InputGroupInput
          id={`create-org-venue-${String(index)}`}
          value={venue.name}
          onChange={(e) => {
            onChangeName(venue.id, e.target.value)
          }}
          placeholder={index === 0 ? 'e.g. The Batcave' : 'Venue name'}
          aria-label={`Venue ${String(index + 1)} name`}
          aria-invalid={invalid}
        />
        {venues.length > 1 && (
          <InputGroupAddon align="inline-end">
            <InputGroupButton
              size="icon-sm"
              aria-label="Remove venue"
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => {
                onRemove(venue.id)
              }}
            >
              <Trash2 />
            </InputGroupButton>
          </InputGroupAddon>
        )}
      </InputGroup>
      <Input
        className="h-10"
        value={venue.url}
        onChange={(e) => {
          onChangeUrl(venue.id, e.target.value)
        }}
        placeholder="Website (optional)"
        aria-label={`Venue ${String(index + 1)} website`}
      />
    </div>
  ))

  // Only scroll once the list is actually taller than fits — an always-on
  // `overflow-y-auto` reserves scrollbar-gutter space even with one row,
  // which shrinks the group short of the fields above it. Most signups add
  // a handful of venues, so this keeps them full-width and only pays the
  // scroll (and its ring-clearance padding) once it's needed.
  if (venues.length <= 3) {
    return <div className="flex flex-col gap-4">{rows}</div>
  }

  return (
    <div className="-mx-2">
      <div className="flex max-h-80 flex-col gap-4 overflow-y-auto p-2 scroll-py-2">
        {rows}
      </div>
    </div>
  )
}

/**
 * Onboarding for a brand-new account with zero organisations: creates the
 * organisation, the requesting user's owner access, and up to 15 venues in
 * one atomic call (`POST /organisations`).
 */
export function CreateOrganisationDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const navigate = useNavigate()
  const user = useUser()
  const fullNameParts = [user?.first, user?.last].filter(Boolean)
  const displayName =
    (user?.full_name?.trim() ? user.full_name : undefined) ??
    (fullNameParts.length > 0 ? fullNameParts.join(' ') : undefined) ??
    user?.email
  const [form, setForm] = useState<FormState>(initialState)
  const [errors, setErrors] = useState<
    Partial<Record<keyof FormState, string>>
  >({})
  const [stage, setStage] = useState<'form' | 'polling'>('form')

  const [createOrganisation, { isLoading: isCreatingOrganisation }] =
    morpheusApi.useCreateSingleOrganisationMutation()
  const [createTrialSubscription, { isLoading: isCreatingTrial }] =
    billingApi.useCreateTrialSubscriptionMutation()
  const [triggerGetSubscription] = billingApi.useLazyGetQuery()
  const isLoading = isCreatingOrganisation || isCreatingTrial

  const handleClose = () => {
    setForm(initialState)
    setErrors({})
    setStage('form')
    onOpenChange(false)
  }

  const addVenue = () => {
    setForm((prev) =>
      prev.venues.length >= MAX_VENUES
        ? prev
        : { ...prev, venues: [...prev.venues, newVenueField()] }
    )
  }

  const removeVenue = (id: string) => {
    setForm((prev) => ({
      ...prev,
      venues: prev.venues.filter((venue) => venue.id !== id),
    }))
  }

  const updateVenueName = (id: string, name: string) => {
    setForm((prev) => ({
      ...prev,
      venues: prev.venues.map((venue) =>
        venue.id === id ? { ...venue, name } : venue
      ),
    }))
  }

  const updateVenueUrl = (id: string, url: string) => {
    setForm((prev) => ({
      ...prev,
      venues: prev.venues.map((venue) =>
        venue.id === id ? { ...venue, url } : venue
      ),
    }))
  }

  // Polls until the subscription webhook has landed, or gives up after a
  // minute — at that point something's genuinely wrong, so stop rather than
  // poll forever.
  const waitForSubscription = async (orgId: string) => {
    const deadline = Date.now() + SUBSCRIPTION_POLL_TIMEOUT_MS
    while (Date.now() < deadline) {
      try {
        await triggerGetSubscription({ orgId }, false).unwrap()
        return true
      } catch {
        // Not written yet — the webhook hasn't landed, keep polling.
      }
      await wait(SUBSCRIPTION_POLL_INTERVAL_MS)
    }
    return false
  }

  const handleSubmit = async () => {
    const name = form.name.trim()
    const website = normaliseWebsite(form.website)
    const venues = form.venues
      .map((venue) => ({ name: venue.name.trim(), url: venue.url.trim() }))
      .filter((venue) => venue.name)
      .map((venue) => ({
        name: venue.name,
        url: venue.url ? normaliseWebsite(venue.url) : undefined,
      }))

    const nextErrors: Partial<Record<keyof FormState, string>> = {}
    if (!name) nextErrors.name = 'Enter your organisation name'
    if (!website) nextErrors.website = 'Enter your website'
    if (venues.length === 0) {
      nextErrors.venues = 'Enter at least one venue name'
    }
    if (!form.companySize) nextErrors.companySize = 'Select your company size'
    if (!form.jobTitle.trim()) nextErrors.jobTitle = 'Enter your job title'
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return

    const referralOrgId = getReferralOrgId()
    const referralCode = form.referralCode.trim()
    // UTMs only: landing_page is the full URL and can carry an authorization
    // code from a login redirect, and referrer/click ids aren't needed here.
    // Cookie first: a sign-up on accounts.* hands its UTMs over that way, as
    // connect's own localStorage is empty for anyone who arrived via accounts.
    const stored = {
      ...readAttribution(ATTRIBUTION_STORAGE_KEY),
      ...readAttributionCookie(),
    }
    const utm: SignupAttribution = {
      ...(stored.utm_source ? { utm_source: stored.utm_source } : {}),
      ...(stored.utm_medium ? { utm_medium: stored.utm_medium } : {}),
      ...(stored.utm_campaign ? { utm_campaign: stored.utm_campaign } : {}),
      ...(stored.utm_term ? { utm_term: stored.utm_term } : {}),
      ...(stored.utm_content ? { utm_content: stored.utm_content } : {}),
    }

    let organisation: OrganizationType
    try {
      organisation = await createOrganisation({
        data: {
          name,
          website,
          venues,
          company_size: form.companySize,
          job_title: form.jobTitle.trim(),
          ...(referralOrgId ? { referral_org_id: referralOrgId } : {}),
          ...(referralCode ? { referral_code: referralCode } : {}),
          ...utm,
        },
      }).unwrap()
      clearAttributionCookie()
    } catch (error) {
      captureException(error)
      toast.error(
        extractErrorMessage(error, 'Failed to create your organisation')
      )
      return
    }

    try {
      await createTrialSubscription({
        orgId: organisation.id,
        data: {
          package: 'full_bhuna',
          venues: venues.length,
          ...(referralOrgId ? { referral_org_id: referralOrgId } : {}),
        },
      }).unwrap()

      setStage('polling')
      const ready = await waitForSubscription(organisation.id)
      if (!ready) {
        toast.error(
          "Your trial is taking longer than expected to set up — hang tight, it'll appear shortly."
        )
      }
    } catch (error) {
      // The org exists either way — surface the trial failure separately so
      // it doesn't read as the whole signup having failed, and still take
      // the user in (support can start the trial manually from here).
      captureException(error)
      toast.error(
        "Your organisation is ready, but we couldn't start your trial — contact support to get it set up."
      )
    }

    handleClose()
    navigate(`/${organisation.id}`)
  }

  const createLabel = isLoading ? 'Creating…' : 'Start free trial'

  if (stage === 'polling') {
    return (
      <Dialog open={open}>
        <DialogContent className="sm:max-w-md" showCloseButton={false}>
          <div className="flex flex-col items-center gap-4 py-6 text-center">
            <Spinner className="size-6" />
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium text-foreground">
                Setting up your trial…
              </p>
              <p className="text-sm text-muted-foreground">
                This can take a moment — we&rsquo;ll take you in as soon as
                it&rsquo;s ready.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose()
      }}
    >
      <DialogContent className="flex max-h-[85dvh] flex-col overflow-hidden sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create your organisation</DialogTitle>
          <DialogDescription>
            30 days, full access, zero faff. Add your venues and you&rsquo;re
            in.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex min-h-0 flex-1 flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            void handleSubmit()
          }}
        >
          <FieldGroup className="-mx-4 min-h-0 flex-1 gap-4 overflow-y-auto px-4 scroll-py-4">
            <div className="flex items-center gap-3">
              <UserAvatar
                user={{
                  first_name: user?.first ?? '',
                  last_name: user?.last ?? '',
                  email: user?.email ?? '',
                }}
              />
              <div className="flex min-w-0 flex-col">
                <span className="truncate text-sm font-medium text-foreground">
                  {displayName}
                </span>
                <span className="truncate text-xs text-muted-foreground">
                  {user?.email}
                </span>
              </div>
            </div>

            <FieldSeparator />

            <div className="grid gap-4 sm:grid-cols-2">
              <Field data-invalid={Boolean(errors.companySize)}>
                <FieldLabel htmlFor="create-org-company-size">
                  Company size
                </FieldLabel>
                <NativeSelect
                  id="create-org-company-size"
                  className="w-full"
                  value={form.companySize}
                  onChange={(e) => {
                    setForm((prev) => ({
                      ...prev,
                      companySize: e.target.value,
                    }))
                  }}
                  aria-invalid={Boolean(errors.companySize)}
                >
                  <option value="">Select…</option>
                  {COMPANY_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size} employees
                    </option>
                  ))}
                </NativeSelect>
                <FieldError>{errors.companySize}</FieldError>
              </Field>

              <Field data-invalid={Boolean(errors.jobTitle)}>
                <FieldLabel htmlFor="create-org-job-title">
                  Job title
                </FieldLabel>
                <Input
                  id="create-org-job-title"
                  value={form.jobTitle}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, jobTitle: e.target.value }))
                  }}
                  placeholder="e.g. Owner"
                  aria-invalid={Boolean(errors.jobTitle)}
                />
                <FieldError>{errors.jobTitle}</FieldError>
              </Field>
            </div>

            <Field data-invalid={Boolean(errors.name)}>
              <FieldLabel htmlFor="create-org-name">
                Organisation name
              </FieldLabel>
              <Input
                id="create-org-name"
                value={form.name}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, name: e.target.value }))
                }}
                placeholder="e.g. Wayne Enterprises"
                aria-invalid={Boolean(errors.name)}
              />
              <FieldError>{errors.name}</FieldError>
            </Field>

            <Field data-invalid={Boolean(errors.website)}>
              <FieldLabel htmlFor="create-org-website">Website</FieldLabel>
              <Input
                id="create-org-website"
                value={form.website}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, website: e.target.value }))
                }}
                placeholder="e.g. https://wayneenterprises.example"
                aria-invalid={Boolean(errors.website)}
              />
              <FieldError>{errors.website}</FieldError>
            </Field>

            <Field data-invalid={Boolean(errors.venues)}>
              <FieldLabel htmlFor="create-org-venue-0">Venues</FieldLabel>
              <VenueFieldsList
                venues={form.venues}
                invalid={Boolean(errors.venues)}
                onChangeName={updateVenueName}
                onChangeUrl={updateVenueUrl}
                onRemove={removeVenue}
              />
              <div className="flex items-center justify-between gap-2">
                <FieldError>{errors.venues}</FieldError>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  leftIcon={<Plus />}
                  disabled={form.venues.length >= MAX_VENUES}
                  onClick={addVenue}
                >
                  Add another venue
                </Button>
              </div>
            </Field>

            <Field>
              <FieldLabel htmlFor="create-org-referral-code">
                Referral code (optional)
              </FieldLabel>
              <Input
                id="create-org-referral-code"
                value={form.referralCode}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    referralCode: e.target.value.toUpperCase(),
                  }))
                }}
                placeholder="e.g. BATCAVE1"
              />
            </Field>
          </FieldGroup>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isLoading}>
              {createLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default CreateOrganisationDialog
